import get from 'just-safe-get';
import React from 'react';
import { FieldValues, useFormContext, UseFormRegister } from 'react-hook-form';
import { notNil } from '../../../core/helpers/is-nil';
import { InlineNotification } from '../../../visual-components/form-inline-notification/form-inline-notification';
import { Input } from '../../../visual-components/input/input';
import { TextArea } from '../../../visual-components/text-area/text-area';
import { FormFieldResponseDtoUnion } from '../../backend/openapi/api-types';
import { useTranslatedNormalizedErrors } from '../../backend/use-translated-errors';
import { WEIGHT_UNIT } from '../../../domain/article/services/weight';
import { getLoggerInstance } from '../../../core/monitoring/logger';
import { FormRadioGroup } from './form-radio-group';
import { FormSelect } from './form-select';

const logger = getLoggerInstance('form-field-input');

type Props = {
  field: FormFieldResponseDtoUnion;
  register: UseFormRegister<FieldValues>;
};

export const FormFieldInput: React.FC<Props> = ({ field, register }) => {
  const { getSafeTranslatedError } = useTranslatedNormalizedErrors();

  const value = 'value' in field ? field.value : '';
  const rules = field.isRequired ? { required: getSafeTranslatedError('required') } : undefined;
  const registerProps = {
    value,
    ...rules,
  };

  const {
    formState: { errors, dirtyFields },
  } = useFormContext();

  const error = get(errors, field.id);
  const hasError = notNil(error?.message);
  const inlineNotification: InlineNotification | undefined = hasError
    ? { type: 'error', message: error.message }
    : undefined;

  const dirty = get(dirtyFields, field.id);
  const isDirty = notNil(dirty);

  switch (field.fieldType) {
    case 'text':
    case 'email':
    case 'number':
      let type: 'text' | 'email' | 'number' | 'password' = 'text';
      let unit: string | null = null;
      if (field.id === 'email') {
        type = 'email';
      } else if (field.id === 'weightKg') {
        type = 'number';
        unit = WEIGHT_UNIT;
      }
      return (
        <Input
          key={field.id}
          className={field.isRequired ? 'mandatory' : ''}
          disabled={field.isDisabled}
          helperMsg={field.helpText || undefined}
          inlineNotification={inlineNotification}
          isDirty={isDirty}
          label={field.label}
          mandatory={field.isRequired}
          placeholder={field.label}
          type={type}
          unit={unit ?? ''}
          {...register(field.id, registerProps)}
        />
      );
    case 'textarea':
      return (
        <TextArea
          key={field.id}
          helperMsg={field.helpText || undefined}
          inlineNotification={inlineNotification}
          isDirty={isDirty}
          label={field.label}
          mandatory={field.isRequired}
          placeholder={field.label}
          {...register(field.id, registerProps)}
        />
      );
    case 'radio':
      if (!('options' in field)) {
        logger.error(`Field with id ${field.id} has fieldType "radio" but is missing "options" property`, {
          field,
        });
        return null;
      }
      return (
        <FormRadioGroup
          key={field.id}
          disabled={field.isDisabled}
          inlineNotification={inlineNotification}
          label={field.label}
          options={field.options}
          required={field.isRequired}
          rules={rules}
          {...register(field.id, registerProps)}
        />
      );
    case 'option':
      if (!('options' in field) || !('placeholder' in field)) {
        logger.error(`Field with id ${field.id} has fieldType "option" but is missing "options" property`, {
          field,
        });
        return null;
      }
      return (
        <FormSelect
          defaultValue={value ?? undefined}
          disabled={field.isDisabled}
          helperMsg={field.helpText || undefined}
          inlineNotification={inlineNotification}
          isDirty={isDirty}
          label={field.label}
          options={field.options}
          placeholder={field.placeholder}
          required={field.isRequired}
          rules={rules}
          {...register(field.id, registerProps)}
        />
      );
  }
};
